// src/utils/avatarFraming.js
// All math in viewport pixels. The viewport is a circle of diameter V.
// frame = { zoom, tx, ty } where tx/ty are pan offsets from center (viewport px).

/** Scale so the image just covers the circle at zoom = 1. */
export function coverScale(naturalW, naturalH, V) {
  // Guard against zero/non-finite inputs (e.g. an image that failed to decode):
  // division would yield Infinity/NaN that cascades through frameScale, clampPan
  // and bakeRegion. Fall back to a neutral scale so the math stays finite.
  if (
    !Number.isFinite(naturalW) || naturalW <= 0 ||
    !Number.isFinite(naturalH) || naturalH <= 0 ||
    !Number.isFinite(V) || V <= 0
  ) {
    return 1;
  }
  return Math.max(V / naturalW, V / naturalH);
}

/** Effective scale at a given zoom. */
export function frameScale(naturalW, naturalH, V, zoom) {
  return coverScale(naturalW, naturalH, V) * zoom;
}

/** Clamp pan so the image always covers the circle (no gaps). tx/ty are VIEWPORT px. */
export function clampPan(tx, ty, naturalW, naturalH, V, zoom) {
  const s = frameScale(naturalW, naturalH, V, zoom);
  const mx = Math.max(0, (naturalW * s - V) / 2);
  const my = Math.max(0, (naturalH * s - V) / 2);
  return {
    tx: Math.min(mx, Math.max(-mx, tx)),
    ty: Math.min(my, Math.max(-my, ty)),
  };
}

/** Clamp zoom into the allowed range. */
export function clampZoom(zoom, min = 1, max = 3) {
  return Math.min(max, Math.max(min, zoom));
}

/**
 * Source rectangle (in image px) that maps to the visible circle.
 * tx/ty are VIEWPORT px (hence divided by s). Returns {sx, sy, sw, sh}.
 */
export function bakeRegion(naturalW, naturalH, V, frame) {
  const { zoom, tx, ty } = frame;
  const s = frameScale(naturalW, naturalH, V, zoom);
  const sw = V / s;
  const sh = V / s;
  const cx = naturalW / 2 - tx / s; // image point shown at viewport center
  const cy = naturalH / 2 - ty / s;
  return { sx: cx - sw / 2, sy: cy - sh / 2, sw, sh };
}

/**
 * Load a File as an orientation-corrected ImageBitmap (EXIF respected),
 * falling back to <img> on engines without createImageBitmap options.
 */
export async function loadOrientedSource(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bmp, width: bmp.width, height: bmp.height };
    } catch {
      /* fall through */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const el = new Image();
      el.onload = () => res(el);
      el.onerror = rej;
      el.src = url;
    });
    return { source: img, width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Draw the framed region into an `out`×`out` canvas and resolve a named JPEG File.
 * Rejects if toBlob yields null. Sets `.preview` (object URL) because the in-form
 * avatar preview (AvatarPreview.jsx) renders `file.preview` for non-string values —
 * without it the avatar goes blank after Save.
 */
export async function bakeAvatarFile(source, naturalW, naturalH, V, frame, out = 400) {
  const { sx, sy, sw, sh } = bakeRegion(naturalW, naturalH, V, frame);
  const canvas = document.createElement('canvas');
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, out, out);
  ctx.drawImage(source, sx, sy, sw, sh, 0, 0, out, out);
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.9));
  if (!blob) throw new Error('avatar bake failed');
  const f = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
  f.shouldRenew = true;
  f.preview = URL.createObjectURL(f);
  return f;
}
